/**
 * Structured DTA Editor Component Displays DTA content in a tabbed, structured format similar to
 * tsDTA templates.
 */

import Alert from "@cloudscape-design/components/alert"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import DatePicker from "@cloudscape-design/components/date-picker"
import FormField from "@cloudscape-design/components/form-field"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Input from "@cloudscape-design/components/input"
import Popover from "@cloudscape-design/components/popover"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import Textarea from "@cloudscape-design/components/textarea"
import {
  borderRadiusItem,
  colorBackgroundContainerContent,
  colorBackgroundDropdownItemHover,
  colorBackgroundItemSelected,
  colorBorderDividerDefault,
  colorTextAccent,
  colorTextBodySecondary,
  colorTextStatusError,
  colorTextStatusWarning,
  spaceScaledS,
  spaceScaledXs,
} from "@cloudscape-design/design-tokens"
import { useState, useEffect, forwardRef, useImperativeHandle } from "react"
import { DateFormat, MetadataVariableFormatEnum } from "@/generated/sdk"
import type { DTADocumentContent } from "../../types/dta"
import type { InlineDiffResult } from "../../utils/inlineDiff"
import { useNotifications } from "../../contexts/NotificationContext"
import { DTADetails } from "../../services/dtaApi"
import { isTableNameSaveError, parseApiErrorMessage } from "../../utils/apiErrors"
import {
  canonicalDateFormat,
  isDateFormatMissing,
  isDateFormatType,
  validateDateFormats,
} from "../../utils/dateFormatValidation"
import { formatUserId } from "../../utils/userFormatter"
import ErrorBoundary from "../ErrorBoundary"
import { CodelistsTab } from "./CodelistsTab"
import { fieldAnchor, useDtaComments } from "./comments/DtaCommentsContext"
import FieldCommentControl from "./comments/FieldCommentControl"
import SectionCommentBar from "./comments/SectionCommentBar"
import { DataQualityTab } from "./DataQualityTab"
import { DiffBadge } from "./DiffBadge"
import { numberValue, toNumber } from "./shared/numberCoercion"
import { ReadOnlyValue } from "./shared/ReadOnlyValue"
import { SDTMLibraryModal } from "./shared/SDTMLibraryModal"
import { TestConceptsTab } from "./TestConceptsTab"
import { testConceptHasNoValueColumn } from "./TestConceptsTab/validation"
import "./DTAEditor.css"

// Dropdown options for a variable's Format, sourced from the API's Format
// vocabulary so the UI can only ever emit a valid value.
const FORMAT_OPTIONS: { value: string; label: string }[] = Object.values(
  MetadataVariableFormatEnum,
).map((v) => ({ value: v, label: v }))

// Per-column decimal scale N for a Decimal column, stored as decimal(38, N).
// Selecting Decimal defaults N to 10 (the conformance fallback); N is editable
// within Iceberg's bounds and matches the ingestion contract's scale range.
const DEFAULT_DECIMAL_SCALE = 10
const DECIMAL_SCALE_MIN = 0
const DECIMAL_SCALE_MAX = 38

// One-line intent per numeric Format for the (i) info affordance next to the
// Format control. Char/Date/Datetime are self-explanatory and omitted.
const FORMAT_HELP: { format: string; text: string }[] = [
  { format: "Integer", text: "whole number (counts, sequence numbers, ids)" },
  { format: "Decimal(N)", text: "exact to N decimal places (measured clinical values)" },
  { format: "Double", text: "approximate; IEEE float, lossy for base-10 fractions" },
]

const DATE_FORMAT_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Unspecified" },
  ...Object.values(DateFormat).map((token) => ({ value: token, label: token })),
]

/**
 * Wraps a field (label + input) so hovering anywhere over it reveals a comment affordance anchored
 * to "<section> / <field>", letting discussions attach to that exact field.
 */
const CommentableField: React.FC<{
  section: string
  field: string
  children: React.ReactNode
}> = ({ section, field, children }) => (
  <FieldCommentControl anchor={fieldAnchor(section, field)} label={field}>
    {children}
  </FieldCommentControl>
)

/**
 * Registers the editor's sub-tab switcher with the comments context, so the rail can switch to an
 * anchor's editor sub-tab when a comment's "View" is clicked. Renders nothing.
 */
const EditorTabBridge: React.FC<{ onSwitchTab: (tabId: string) => void }> = ({ onSwitchTab }) => {
  const comments = useDtaComments()
  const setEditorTabSwitcher = comments?.setEditorTabSwitcher
  useEffect(() => {
    setEditorTabSwitcher?.(onSwitchTab)
  }, [setEditorTabSwitcher, onSwitchTab])
  return null
}

interface DTAEditorProps {
  dta: DTADetails
  onSave?: (updatedContent: DTADocumentContent) => Promise<void>
  onHasChangesChange?: (hasChanges: boolean) => void
  canEdit?: boolean
  tableNameLocked?: boolean
  isSaving?: boolean
  initialTab?: string
  onTabChange?: (tabId: string) => void
  inlineDiff?: InlineDiffResult | null
}

export interface DTAEditorHandle {
  getContent: () => DTADocumentContent
  triggerSave: () => Promise<void>
  navigateToTab: (tabId: string, field?: string) => void
  /**
   * Run all validations (save + publish readiness). Returns errors or empty array.
   */
  validateForPublish: () => Array<{ field: string; tab: string; message: string }>
}

interface TabDefinition {
  id: string
  label: string
  shortLabel: string
  category: "instructional" | "required" | "optional"
  component: React.ComponentType<TabContentProps>
  /**
   * Shown under the tab title in the content card header. The title itself renders only there — tab
   * components must not repeat it (#504).
   */
  description?: string
}

interface TabContentProps {
  dta: DTADetails
  content: DTADocumentContent
  onUpdate: (updates: Partial<DTADocumentContent>) => void
  canEdit: boolean
  tableNameLocked?: boolean
  showErrors?: boolean
  inlineDiff?: InlineDiffResult | null
}

const REQUIRED_MARK = (
  <Box variant="span" color="text-status-error">
    *
  </Box>
)

// Tab Components
const UserGuideTab: React.FC<TabContentProps> = ({ dta, content, onUpdate, canEdit }) => {
  const userGuide = content.user_guide ?? {}
  const SECTION = "User Guide"
  // Type and Date are display-only derived values (never editable). In edit mode they keep their
  // disabled-input appearance for form consistency; in read-only mode they render as plain text.
  const typeValue = userGuide.type || `${dta.domains?.[0] || "Domain"} v${dta.version}`
  const dateValue = new Date().toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })

  return (
    <SpaceBetween size="l">
      <Container>
        <ColumnLayout columns={2}>
          <CommentableField section={SECTION} field="Form ID">
            <FormField label="Form ID">
              {canEdit ? (
                <Input
                  value={userGuide.form_id || ""}
                  onChange={({ detail }) =>
                    onUpdate({ user_guide: { ...userGuide, form_id: detail.value } })
                  }
                />
              ) : (
                <ReadOnlyValue value={userGuide.form_id} />
              )}
            </FormField>
          </CommentableField>
          <FormField label="Type">
            {canEdit ? (
              <Input value={typeValue} disabled readOnly />
            ) : (
              <ReadOnlyValue value={typeValue} />
            )}
          </FormField>
          <FormField label="Date">
            {canEdit ? (
              <Input value={dateValue} disabled readOnly />
            ) : (
              <ReadOnlyValue value={dateValue} />
            )}
          </FormField>
        </ColumnLayout>
      </Container>

      <Container header={<Header variant="h2">Trial Information</Header>}>
        <ColumnLayout columns={2}>
          <CommentableField section={SECTION} field="Trial ID">
            <FormField label={<>Trial ID {REQUIRED_MARK}</>}>
              {canEdit ? (
                <Input value={dta.study_id} disabled readOnly placeholder="Specify Trial ID" />
              ) : (
                <ReadOnlyValue value={dta.study_id} />
              )}
              {typeof content.parsed_study_id === "string" &&
                content.parsed_study_id !== dta.study_id && (
                  <Box variant="small" color="text-status-warning" margin={{ top: "xxs" }}>
                    <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
                      <Icon name="status-warning" size="normal" />
                      <span>Document references study &quot;{content.parsed_study_id}&quot;</span>
                    </SpaceBetween>
                  </Box>
                )}
            </FormField>
          </CommentableField>
          <FormField label="Domain">
            {canEdit ? (
              <Input value={dta.domains?.[0] || ""} disabled readOnly />
            ) : (
              <ReadOnlyValue value={dta.domains?.[0]} />
            )}
          </FormField>
          <FormField label="(E)DP Name">
            {canEdit ? (
              <Input value={dta.vendor_name || ""} disabled readOnly />
            ) : (
              <ReadOnlyValue value={dta.vendor_name} />
            )}
          </FormField>
          <CommentableField section={SECTION} field="SDTM Library">
            <FormField label="SDTM Library">
              {canEdit ? (
                <Input
                  value={userGuide.sdtm_library || ""}
                  onChange={({ detail }) =>
                    onUpdate({ user_guide: { ...userGuide, sdtm_library: detail.value } })
                  }
                />
              ) : (
                <ReadOnlyValue value={userGuide.sdtm_library} />
              )}
            </FormField>
          </CommentableField>
          <FormField label={"SDTM Metadata Release Date"}>
            {canEdit ? (
              <DatePicker
                value={userGuide.sdtm_release_date || ""}
                onChange={({ detail }) =>
                  onUpdate({ user_guide: { ...userGuide, sdtm_release_date: detail.value } })
                }
                placeholder="YYYY/MM/DD"
                openCalendarAriaLabel={(selectedDate) =>
                  "Choose date" + (selectedDate ? `, selected date is ${selectedDate}` : "")
                }
              />
            ) : (
              <ReadOnlyValue value={userGuide.sdtm_release_date} />
            )}
          </FormField>
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  )
}

interface FileTransferConfig {
  source_folder?: string
  file_name?: string
  file_definition?: string
  destination_folder?: string
  cumulative_incremental?: string
  comments?: string
}

interface TransferMetadata {
  blinding_security?: string
  frequency?: string
  connection_type?: string
  dummy?: FileTransferConfig
  prod?: FileTransferConfig
}

const DEFAULT_TRANSFER_CONFIG: { dummy: FileTransferConfig; prod: FileTransferConfig } = {
  dummy: {
    source_folder: "",
    file_name: "",
    file_definition: "",
    destination_folder: "",
    cumulative_incremental: "",
    comments: "",
  },
  prod: {
    source_folder: "",
    file_name: "",
    file_definition: "",
    destination_folder: "",
    cumulative_incremental: "",
    comments: "",
  },
}

const toOption = (value: string | undefined) => (value ? { value, label: value } : null)

const DataIngestionTab: React.FC<TabContentProps> = ({ content, onUpdate, canEdit }) => {
  const ingestion = (content.transfer_metadata || {}) as TransferMetadata
  const SECTION = "Data Ingestion Parameters"

  const getEnvConfig = (env: "dummy" | "prod"): FileTransferConfig => ({
    ...DEFAULT_TRANSFER_CONFIG[env],
    ...ingestion[env],
  })

  const updateEnvConfig = (
    env: "dummy" | "prod",
    field: keyof FileTransferConfig,
    value: string,
  ) => {
    onUpdate({
      transfer_metadata: {
        ...ingestion,
        [env]: { ...getEnvConfig(env), [field]: value },
      },
    })
  }

  const fileConfigItems = (["dummy", "prod"] as const).map((env) => ({
    env,
    label: env === "dummy" ? "Dummy" : "Prod",
    config: getEnvConfig(env),
  }))

  const fileConfigColumns: TableProps.ColumnDefinition<(typeof fileConfigItems)[number]>[] = [
    {
      id: "environment",
      header: "Environment",
      cell: (item) => <Box fontWeight="bold">{item.label}</Box>,
      width: 100,
    },
    {
      id: "source_folder",
      header: "Source Folder Path",
      cell: (item) =>
        canEdit ? (
          <Textarea
            value={item.config.source_folder || ""}
            onChange={({ detail }) => updateEnvConfig(item.env, "source_folder", detail.value)}
            rows={2}
          />
        ) : (
          <ReadOnlyValue value={item.config.source_folder} />
        ),
    },
    {
      id: "file_name",
      header: "File Name",
      cell: (item) =>
        canEdit ? (
          <Input
            value={item.config.file_name || ""}
            onChange={({ detail }) => updateEnvConfig(item.env, "file_name", detail.value)}
          />
        ) : (
          <ReadOnlyValue value={item.config.file_name} />
        ),
    },
    {
      id: "file_definition",
      header: "File Definition",
      cell: (item) =>
        canEdit ? (
          <Input
            value={item.config.file_definition || ""}
            onChange={({ detail }) => updateEnvConfig(item.env, "file_definition", detail.value)}
          />
        ) : (
          <ReadOnlyValue value={item.config.file_definition} />
        ),
    },
    {
      id: "destination_folder",
      header: "Destination Folder Path",
      cell: (item) =>
        canEdit ? (
          <Textarea
            value={item.config.destination_folder || ""}
            onChange={({ detail }) => updateEnvConfig(item.env, "destination_folder", detail.value)}
            rows={2}
          />
        ) : (
          <ReadOnlyValue value={item.config.destination_folder} />
        ),
    },
    {
      id: "cumulative_incremental",
      header: "Cumulative/Incremental",
      cell: (item) =>
        canEdit ? (
          <Select
            selectedOption={toOption(item.config.cumulative_incremental)}
            placeholder="Select..."
            expandToViewport
            onChange={({ detail }) =>
              updateEnvConfig(item.env, "cumulative_incremental", detail.selectedOption.value ?? "")
            }
            options={[
              { value: "Cumulative", label: "Cumulative" },
              { value: "Incremental", label: "Incremental" },
            ]}
          />
        ) : (
          <ReadOnlyValue value={item.config.cumulative_incremental} />
        ),
    },
    {
      id: "comments",
      header: "Comments",
      cell: (item) =>
        canEdit ? (
          <Textarea
            value={item.config.comments || ""}
            onChange={({ detail }) => updateEnvConfig(item.env, "comments", detail.value)}
            rows={2}
          />
        ) : (
          <ReadOnlyValue value={item.config.comments} />
        ),
    },
  ]

  return (
    <SpaceBetween size="m">
      <Container>
        <SpaceBetween size="l">
          <Header variant="h3">Transfer Parameters</Header>
          <ColumnLayout columns={3}>
            <CommentableField section={SECTION} field="Blinding/Security">
              <FormField label="Blinding/Security">
                {canEdit ? (
                  <Select
                    selectedOption={toOption(ingestion.blinding_security)}
                    placeholder="Select..."
                    onChange={({ detail }) =>
                      onUpdate({
                        transfer_metadata: {
                          ...ingestion,
                          blinding_security: detail.selectedOption.value ?? "",
                        },
                      })
                    }
                    options={[
                      { value: "No secure data involved", label: "No secure data involved" },
                      { value: "Secure data involved", label: "Secure data involved" },
                    ]}
                  />
                ) : (
                  <ReadOnlyValue value={ingestion.blinding_security} />
                )}
              </FormField>
            </CommentableField>
            <CommentableField section={SECTION} field="Transfer Frequency">
              <FormField label="Transfer Frequency">
                {canEdit ? (
                  <Select
                    selectedOption={toOption(ingestion.frequency)}
                    placeholder="Select..."
                    onChange={({ detail }) =>
                      onUpdate({
                        transfer_metadata: {
                          ...ingestion,
                          frequency: detail.selectedOption.value ?? "",
                        },
                      })
                    }
                    options={[
                      { value: "Daily", label: "Daily" },
                      { value: "Weekly", label: "Weekly" },
                      { value: "Monthly", label: "Monthly" },
                      { value: "On-demand", label: "On-demand" },
                    ]}
                  />
                ) : (
                  <ReadOnlyValue value={ingestion.frequency} />
                )}
              </FormField>
            </CommentableField>
            <CommentableField section={SECTION} field="Method of Transfer">
              <FormField label="Method of Transfer">
                {canEdit ? (
                  <Select
                    selectedOption={toOption(ingestion.connection_type)}
                    placeholder="Select..."
                    onChange={({ detail }) =>
                      onUpdate({
                        transfer_metadata: {
                          ...ingestion,
                          connection_type: detail.selectedOption.value ?? "",
                        },
                      })
                    }
                    options={[
                      { value: "Autoload", label: "Autoload" },
                      { value: "sFTP", label: "sFTP" },
                      { value: "API", label: "API" },
                      { value: "Manual", label: "Manual" },
                    ]}
                  />
                ) : (
                  <ReadOnlyValue value={ingestion.connection_type} />
                )}
              </FormField>
            </CommentableField>
          </ColumnLayout>

          <Table
            variant="embedded"
            header={<Header variant="h3">Transfer File Configuration</Header>}
            columnDefinitions={fileConfigColumns}
            items={fileConfigItems}
            trackBy={(item) => item.env}
          />
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  )
}

const TransferMetadataTab: React.FC<TabContentProps> = ({
  dta,
  content,
  onUpdate,
  canEdit,
  inlineDiff,
}) => {
  const schema = content.transfer_metadata_schema ?? { variables: [] }
  const variables = schema.variables ?? []
  const metadata = content.transfer_metadata || {}
  const sourceNote = (schema.sheet || "").toUpperCase().includes("SAMPTRAC")
    ? "LabCorp SAMPTRAC template."
    : "Standard transfer metadata template."
  const [sdtmModalOpen, setSdtmModalOpen] = useState(false)
  const [sdtmModalVariableIndex, setSdtmModalVariableIndex] = useState<number | null>(null)

  const handleVariableUpdate = (
    index: number,
    field: string,
    value: string | number | boolean | undefined,
  ) => {
    const updatedVariables = [...variables]
    updatedVariables[index] = { ...updatedVariables[index], [field]: value }
    onUpdate({
      transfer_metadata_schema: {
        ...schema,
        variables: updatedVariables,
      },
    })
  }

  // Changing Format also owns the paired decimal_places: Decimal defaults its
  // scale to 10 (editable), and any other Format drops a stale scale so the
  // saved payload only carries decimal_places on a Decimal column.
  const handleFormatChange = (index: number, format: MetadataVariableFormatEnum) => {
    const updatedVariables = [...variables]
    const next = { ...updatedVariables[index], format }
    if (format === MetadataVariableFormatEnum.Decimal) {
      if (next.decimal_places == null) next.decimal_places = DEFAULT_DECIMAL_SCALE
    } else {
      delete next.decimal_places
    }
    updatedVariables[index] = next
    onUpdate({
      transfer_metadata_schema: {
        ...schema,
        variables: updatedVariables,
      },
    })
  }

  const handleAddVariable = () => {
    const newOrder =
      variables.length > 0 ? Math.max(...variables.map((v) => Number(v.order) || 0)) + 1 : 1
    onUpdate({
      transfer_metadata_schema: {
        ...schema,
        variables: [...variables, { order: newOrder, variable_name: "", variable_label: "" }],
      },
    })
  }

  const handleRemoveVariable = (index: number) => {
    const updatedVariables = variables.filter((_, i) => i !== index)
    onUpdate({
      transfer_metadata_schema: {
        ...schema,
        variables: updatedVariables,
      },
    })
  }

  const buildCodelistOptions = (v: (typeof variables)[number]) => {
    const customCodelistOptions = ((content.code_lists || []) as Array<{ name: string }>).map(
      (cl) => ({
        value: cl.name,
        label: cl.name,
      }),
    )
    const sdtmOptions: { value: string; label: string; disabled?: boolean }[] = [
      { value: "__separator__", label: "── SDTM Codelists ──", disabled: true },
    ]
    const ref = v.codelist_ref || ""
    const customNames = ((content.code_lists || []) as Array<{ name: string }>).map((cl) => cl.name)
    if (ref && !customNames.includes(ref)) {
      sdtmOptions.push({ value: ref, label: ref })
    }
    sdtmOptions.push({ value: "__sdtm_browse__", label: "Browse SDTM Codelist Library..." })
    return [{ value: "", label: "-" }, ...customCodelistOptions, ...sdtmOptions]
  }

  type VariableRow = (typeof variables)[number] & { _index: number }

  const variableColumns: TableProps.ColumnDefinition<VariableRow>[] = [
    {
      id: "order",
      header: "#",
      cell: (item) => <Box textAlign="center">{item.order ?? ""}</Box>,
      width: 50,
    },
    {
      id: "variable_name",
      header: <>Variable name {REQUIRED_MARK}</>,
      cell: (item) => (
        <div>
          {canEdit ? (
            <Input
              value={item.variable_name ?? ""}
              onChange={({ detail }) =>
                handleVariableUpdate(item._index, "variable_name", detail.value)
              }
            />
          ) : (
            <ReadOnlyValue value={item.variable_name} />
          )}
          {inlineDiff?.schema.get(item.variable_name ?? "") && (
            <DiffBadge
              status={inlineDiff.schema.get(item.variable_name ?? "")!}
              version={dta.version}
              dtaId={dta.dta_id}
              section="schema"
              item={item.variable_name ?? ""}
            />
          )}
        </div>
      ),
      minWidth: 150,
    },
    {
      id: "variable_label",
      header: "Variable label",
      cell: (item) =>
        canEdit ? (
          <Input
            value={item.variable_label ?? ""}
            onChange={({ detail }) =>
              handleVariableUpdate(item._index, "variable_label", detail.value)
            }
          />
        ) : (
          <ReadOnlyValue value={item.variable_label} />
        ),
      minWidth: 180,
    },
    {
      id: "format",
      header: (
        <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
          <span>Data type</span>
          <Popover
            header="Data types"
            triggerType="custom"
            position="top"
            content={
              <SpaceBetween size="xxs">
                {FORMAT_HELP.map((f) => (
                  <div key={f.format}>
                    <Box variant="strong" display="inline">
                      {f.format}
                    </Box>
                    {`: ${f.text}`}
                  </div>
                ))}
              </SpaceBetween>
            }
          >
            <Button variant="inline-icon" iconName="status-info" ariaLabel="Data type help" />
          </Popover>
        </SpaceBetween>
      ),
      cell: (item) => {
        if (!canEdit) {
          return (
            <ReadOnlyValue
              value={
                item.format === MetadataVariableFormatEnum.Decimal
                  ? `Decimal(${item.decimal_places ?? DEFAULT_DECIMAL_SCALE})`
                  : item.format
              }
            />
          )
        }
        const isDecimal = item.format === MetadataVariableFormatEnum.Decimal
        const scale = item.decimal_places
        const scaleInvalid =
          scale != null && (scale < DECIMAL_SCALE_MIN || scale > DECIMAL_SCALE_MAX)
        return (
          <SpaceBetween size="xxs">
            <Select
              selectedOption={item.format ? { value: item.format, label: item.format } : null}
              options={FORMAT_OPTIONS}
              placeholder="Select..."
              // Render the dropdown in a portal so the table's overflow does not clip it.
              expandToViewport
              onChange={({ detail }) =>
                handleFormatChange(
                  item._index,
                  (detail.selectedOption.value ?? "") as MetadataVariableFormatEnum,
                )
              }
            />
            {isDecimal ? (
              <FormField label="Decimal places (N)">
                <Input
                  type="number"
                  value={numberValue(scale)}
                  invalid={scaleInvalid}
                  onChange={({ detail }) =>
                    handleVariableUpdate(item._index, "decimal_places", toNumber(detail.value))
                  }
                />
              </FormField>
            ) : null}
          </SpaceBetween>
        )
      },
      // Fixed width so selecting a longer option (e.g. "Datetime") or showing the
      // Decimal precision input does not resize the column.
      width: 160,
    },
    {
      id: "data_format",
      header: "Format",
      cell: (item) => {
        if (!isDateFormatType(item.format)) {
          return <Box color="text-status-inactive">—</Box>
        }
        const current = item.data_format ?? ""
        const canonical = canonicalDateFormat(current)
        const known = canonical ? DATE_FORMAT_OPTIONS.find((o) => o.value === canonical) : undefined
        // Highlight AI extracted invalid value
        const isUnsupported = current.trim() !== "" && !known
        // Blank date format on a Date/Datetime column is an incomplete required field (#1252).
        const isMissing = isDateFormatMissing(item)
        const selectedOption =
          known ??
          (current ? { value: current, label: `${current} (unsupported)` } : DATE_FORMAT_OPTIONS[0])
        return canEdit ? (
          <FormField errorText={isMissing ? "Date format required" : undefined}>
            <Select
              selectedOption={selectedOption}
              options={DATE_FORMAT_OPTIONS}
              expandToViewport
              invalid={isUnsupported || isMissing}
              onChange={({ detail }) =>
                handleVariableUpdate(item._index, "data_format", detail.selectedOption.value ?? "")
              }
            />
          </FormField>
        ) : (
          <ReadOnlyValue value={item.data_format || "Unspecified"} />
        )
      },
      width: 240,
    },
    {
      id: "max_length",
      header: "Max length",
      cell: (item) =>
        canEdit ? (
          <Input
            type="number"
            value={numberValue(item.max_length)}
            onChange={({ detail }) =>
              handleVariableUpdate(item._index, "max_length", toNumber(detail.value))
            }
          />
        ) : (
          <ReadOnlyValue value={item.max_length} />
        ),
      minWidth: 80,
    },
    {
      id: "description",
      header: "Description",
      cell: (item) =>
        canEdit ? (
          <Textarea
            value={item.description ?? ""}
            onChange={({ detail }) =>
              handleVariableUpdate(item._index, "description", detail.value)
            }
            rows={2}
          />
        ) : (
          <ReadOnlyValue value={item.description} />
        ),
      minWidth: 300,
    },
    {
      id: "unique_identifier",
      header: "Unique Sample Assessment Identifier",
      cell: (item) => (
        <Box textAlign="center">
          {canEdit ? (
            <Checkbox
              checked={Boolean(item.unique_identifier)}
              onChange={({ detail }) =>
                handleVariableUpdate(item._index, "unique_identifier", detail.checked)
              }
            />
          ) : (
            <ReadOnlyValue value={item.unique_identifier ? "Yes" : "No"} />
          )}
        </Box>
      ),
    },
    {
      id: "populate_for_all",
      header: "Populate For All Records",
      cell: (item) => (
        <Box textAlign="center">
          {canEdit ? (
            <Checkbox
              checked={Boolean(item.populate_for_all)}
              onChange={({ detail }) =>
                handleVariableUpdate(item._index, "populate_for_all", detail.checked)
              }
            />
          ) : (
            <ReadOnlyValue value={item.populate_for_all ? "Yes" : "No"} />
          )}
        </Box>
      ),
    },
    {
      id: "codelist",
      header: "Codelist",
      cell: (item) => {
        if (!canEdit) {
          return <ReadOnlyValue value={item.codelist_ref} />
        }
        const options = buildCodelistOptions(item)
        return (
          <Select
            selectedOption={
              item.codelist_ref
                ? { value: item.codelist_ref, label: item.codelist_ref }
                : { value: "", label: "-" }
            }
            onChange={({ detail }) => {
              const val = detail.selectedOption.value ?? ""
              if (val === "__sdtm_browse__") {
                setSdtmModalVariableIndex(item._index)
                setSdtmModalOpen(true)
              } else {
                const updatedVariables = [...variables]
                updatedVariables[item._index] = {
                  ...updatedVariables[item._index],
                  codelist_ref: val,
                  codelist_source: undefined,
                  codelist_code: undefined,
                }
                onUpdate({
                  transfer_metadata_schema: { ...schema, variables: updatedVariables },
                })
              }
            }}
            options={options}
            expandToViewport
          />
        )
      },
      minWidth: 300,
    },
    ...(canEdit
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: (item: VariableRow) => (
              <Box textAlign="center">
                <Button
                  variant="icon"
                  iconName="close"
                  onClick={() => handleRemoveVariable(item._index)}
                  ariaLabel="Remove attribute"
                />
              </Box>
            ),
            width: 70,
          } satisfies TableProps.ColumnDefinition<VariableRow>,
        ]
      : []),
  ]

  const variableItems: VariableRow[] = variables.map((v, i) => ({ ...v, _index: i }))
  const pkInferred = Boolean((content as Record<string, unknown>).pk_inferred)

  const unsupportedDateFields = variables
    .filter((v) => {
      const df = (v.data_format || "").trim()
      return isDateFormatType(v.format) && df !== "" && !canonicalDateFormat(df)
    })
    .map((v) => (v.variable_name || "").trim() || "(unnamed)")

  return (
    <>
      {pkInferred && (
        <Alert type="warning">
          <strong>Primary key columns were auto-inferred</strong> and require your review. Check the
          "Unique Identifier" column below to confirm or correct the primary key selection.
        </Alert>
      )}
      {unsupportedDateFields.length > 0 && (
        <Alert type="warning">
          <strong>Unrecognized date format</strong> on {unsupportedDateFields.join(", ")}. Pick a
          supported option in the "Format" column.
        </Alert>
      )}
      <Container
        header={
          <Header
            variant="h3"
            description={sourceNote}
            actions={canEdit && <Button onClick={handleAddVariable}>+ Add Attribute</Button>}
          />
        }
      >
        {variables.length > 0 ? (
          <div className="metadata-variables-table">
            <Table
              variant="embedded"
              stickyHeader
              stickyColumns={{ last: 1 }}
              columnDefinitions={variableColumns}
              items={variableItems}
              trackBy={(item) => String(item._index)}
            />
          </div>
        ) : Object.keys(metadata).length > 0 ? (
          <Box padding="s" variant="code">
            <pre className="overflow-x-auto text-sm">{JSON.stringify(metadata, null, 2)}</pre>
          </Box>
        ) : (
          <Box textAlign="center" color="text-status-inactive" padding={{ vertical: "xl" }}>
            <SpaceBetween size="s">
              <span>No transfer metadata schema or variables defined for this DTA.</span>
              {canEdit && <Button onClick={handleAddVariable}>+ Add First Attribute</Button>}
            </SpaceBetween>
          </Box>
        )}
        <SDTMLibraryModal
          isOpen={sdtmModalOpen}
          onClose={() => setSdtmModalOpen(false)}
          onImport={(items) => {
            if (sdtmModalVariableIndex !== null && items.length > 0) {
              const item = items[0]
              const updatedVariables = [...variables]
              updatedVariables[sdtmModalVariableIndex] = {
                ...updatedVariables[sdtmModalVariableIndex],
                codelist_ref: item.name,
                codelist_source: "sdtm",
                codelist_code: item.source_id || undefined,
              }
              onUpdate({
                transfer_metadata_schema: { ...schema, variables: updatedVariables },
              })
            }
            setSdtmModalOpen(false)
          }}
          existingNames={((content.code_lists || []) as Array<{ name: string }>).map(
            (cl) => cl.name,
          )}
          mode="pick"
        />
      </Container>
    </>
  )
}

// @ts-expect-error Removing this tab from being displayed for now, but not deleting it in case we want it back
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ConformanceChecksTab: React.FC<TabContentProps> = ({ dta }) => {
  if (!dta.domains || dta.domains.length === 0) {
    return (
      <Container header={<Header variant="h2">Conformance Checks</Header>}>
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: "xl" }}>
          No domain available for conformance checks.
        </Box>
      </Container>
    )
  }

  return (
    <Container
      header={
        <Header variant="h2" description="Configure and manage data ingestion conformance checks">
          Conformance Checks
        </Header>
      }
    >
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <Button
          variant="primary"
          iconName="status-positive"
          href={`/dtas/${dta.dta_id}/domains/${dta.domains[0]}/check-configurations`}
        >
          Open Conformance Checks
        </Button>
      </Box>
    </Container>
  )
}

const WorkflowHistoryTab: React.FC<TabContentProps> = ({ dta }) => {
  return (
    <SpaceBetween size="s">
      {dta.status_history && dta.status_history.length > 0 ? (
        dta.status_history.map((history) => (
          <Container key={history.history_id}>
            <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
              <SpaceBetween size="xxs">
                <Box fontWeight="bold">
                  {history.from_status
                    ? `${history.from_status} → ${history.to_status}`
                    : `Created as ${history.to_status}`}
                </Box>
                <Box fontSize="body-s" color="text-body-secondary">
                  {new Date(history.changed_at).toLocaleString()} by{" "}
                  {formatUserId(history.changed_by)}
                </Box>
                {history.reason && (
                  <Box fontSize="body-s" color="text-body-secondary">
                    Reason: {history.reason}
                  </Box>
                )}
              </SpaceBetween>
              <Box textAlign="right">
                <Badge color="grey">{history.workflow_stage}</Badge>
              </Box>
            </Grid>
          </Container>
        ))
      ) : (
        <Container>
          <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
            <SpaceBetween size="xxs">
              <Box fontWeight="bold">Version {dta.version}</Box>
              <Box fontSize="body-s" color="text-body-secondary">
                Created {new Date(dta.created_at).toLocaleString()} by{" "}
                {formatUserId(dta.created_by)}
              </Box>
              {dta.last_modified_at && dta.last_modified_at !== dta.created_at && (
                <Box fontSize="body-s" color="text-body-secondary">
                  Last modified {new Date(dta.last_modified_at).toLocaleString()} by{" "}
                  {formatUserId(dta.last_modified_by)}
                </Box>
              )}
            </SpaceBetween>
            <Box textAlign="right">
              <Badge color="grey">{dta.workflow_stage}</Badge>
            </Box>
          </Grid>
        </Container>
      )}
    </SpaceBetween>
  )
}

// Tab definitions
const TAB_DEFINITIONS: TabDefinition[] = [
  {
    id: "user-guide",
    label: "User Guide",
    shortLabel: "UG",
    category: "instructional",
    component: UserGuideTab,
  },
  {
    id: "version-history",
    label: "Workflow History",
    shortLabel: "VH",
    category: "instructional",
    component: WorkflowHistoryTab,
  },
  {
    id: "data-ingestion",
    label: "Data Ingestion Parameters",
    shortLabel: "DI",
    category: "required",
    component: DataIngestionTab,
    description: "Transfer settings and file configuration",
  },
  {
    id: "transfer-metadata",
    label: "Metadata",
    shortLabel: "ST",
    category: "required",
    component: TransferMetadataTab,
  },
  {
    id: "test-concepts",
    label: "Test Concepts",
    shortLabel: "TC",
    category: "required",
    component: TestConceptsTab,
    description: "Define conditional validation rules for column values.",
  },
  {
    id: "data-quality-rules",
    label: "Data Quality Rules",
    shortLabel: "DQ",
    category: "required",
    component: DataQualityTab,
    description: "Define file validation rules and data quality checks",
  },
  {
    id: "codelists",
    label: "Codelists",
    shortLabel: "CL",
    category: "required",
    component: CodelistsTab,
    description:
      "Define the approved values for categorical data columns. During ingestion, any vendor data value not on the list will be flagged based on the severity you set.",
  },
]

/**
 * Left-rail tab navigation.
 *
 * This is a VERTICAL, GROUPED, status-annotated rail, so Cloudscape `Tabs` (a horizontal, ungrouped
 * strip) is the wrong primitive. Cloudscape `SideNavigation` is vertical with sections, but it
 * forces `href`/anchor navigation semantics (these are in-component sub-tab switches, not routes)
 * and only allows a `Badge`/"New" label in its `info` slot, which cannot express the 40x40
 * category-colored short-label square each item shows. So the rail is a small custom component
 * built from Cloudscape primitives (`Box`, design tokens) over a native `<button>`, which keeps
 * correct button semantics (and keyboard/focus) for tab switching. The only bespoke pieces are the
 * category square and the status dot, which have no Cloudscape token/primitive.
 */

// Category square colors: a bespoke categorical scheme with no Cloudscape token equivalent, kept
// as constants to preserve the prior appearance (Tailwind rose-700 / teal-600 / stone-500).
const CATEGORY_BADGE_COLOR: Record<TabDefinition["category"], string> = {
  instructional: "#be123c",
  required: "#0d9488",
  optional: "#78716c",
}

const NAV_BADGE_STYLE: React.CSSProperties = {
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  borderRadius: borderRadiusItem,
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 700,
}

const NAV_DOT_STYLE: React.CSSProperties = {
  marginInlineStart: "auto",
  flexShrink: 0,
  width: "10px",
  height: "10px",
  borderRadius: "50%",
}

/**
 * A single tab entry in the left rail: category square + label + optional status dot. Renders a red
 * dot when the tab has validation errors, else an amber dot when it has unsaved changes (error
 * takes precedence, matching the prior behavior).
 */
const EditorNavItem: React.FC<{
  tab: TabDefinition
  label: string
  active: boolean
  hasError: boolean
  isDirty: boolean
  onSelect: () => void
}> = ({ tab, label, active, hasError, isDirty, onSelect }) => {
  const [hovered, setHovered] = useState(false)
  const background = active
    ? colorBackgroundItemSelected
    : hovered
      ? colorBackgroundDropdownItemHover
      : "transparent"
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: spaceScaledS,
        width: "100%",
        padding: `${spaceScaledXs} ${spaceScaledS}`,
        border: "none",
        borderRadius: borderRadiusItem,
        background,
        color: active ? colorTextAccent : colorTextBodySecondary,
        fontWeight: active ? 700 : 400,
        fontSize: "14px",
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <span
        aria-hidden="true"
        style={{ ...NAV_BADGE_STYLE, background: CATEGORY_BADGE_COLOR[tab.category] }}
      >
        {tab.shortLabel}
      </span>
      <span style={{ flex: 1, textAlign: "left" }}>{label}</span>
      {hasError ? (
        <span
          title="Has validation errors"
          style={{ ...NAV_DOT_STYLE, background: colorTextStatusError }}
        />
      ) : isDirty ? (
        <span
          title="Has unsaved changes"
          style={{ ...NAV_DOT_STYLE, background: colorTextStatusWarning }}
        />
      ) : null}
    </button>
  )
}

const DTAEditor = forwardRef<DTAEditorHandle, DTAEditorProps>(
  (
    {
      dta,
      onSave,
      onHasChangesChange,
      canEdit = false,
      tableNameLocked = false,
      isSaving = false,
      initialTab,
      onTabChange,
      inlineDiff,
    },
    ref,
  ) => {
    const { showToast } = useNotifications()
    const [activeTab, setActiveTab] = useState<string>(initialTab || "user-guide")
    const [editedContent, setEditedContent] = useState<DTADocumentContent>(
      dta.document_content || {},
    )
    const [hasChanges, setHasChanges] = useState(false)
    const [dataQualityValid, setDataQualityValid] = useState(true)
    const [dataQualityErrors, setDataQualityErrors] = useState<
      Record<string, unknown> | undefined
    >()
    const [terminologyValidationFailed, setTerminologyValidationFailed] = useState(false)
    const [saveValidationError, setSaveValidationError] = useState<string | null>(null)
    const [serverTableNameError, setServerTableNameError] = useState<string | undefined>()

    useEffect(() => {
      setEditedContent(dta.document_content || {})
      setHasChanges(false)
    }, [dta.document_content])

    useEffect(() => {
      onHasChangesChange?.(hasChanges)
    }, [hasChanges, onHasChangesChange])

    const handleUpdate = (updates: Partial<DTADocumentContent>) => {
      setEditedContent((prev) => {
        // Only mark dirty if content actually changed
        const hasRealChange = Object.entries(updates).some(
          ([key, value]) =>
            JSON.stringify(prev[key as keyof DTADocumentContent]) !== JSON.stringify(value),
        )
        if (hasRealChange) {
          setHasChanges(true)
        }
        const next = { ...prev, ...updates }

        // TODO(#385): Consolidate unique_identifier and table.primaryKeyFields into a single
        // source of truth. For now, metadata unique_identifier is authoritative and syncs
        // one-way to table.primaryKeyFields.

        // Sync: unique_identifier flags → table.primaryKeyFields
        if (updates.transfer_metadata_schema) {
          const vars = (
            updates.transfer_metadata_schema as { variables?: Array<Record<string, unknown>> }
          )?.variables
          if (vars) {
            const pkFromMetadata = vars
              .filter(
                (v) =>
                  (v.unique_identifier === true ||
                    v.unique_identifier === "true" ||
                    v.unique_identifier === "X") &&
                  v.variable_name,
              )
              .map((v) => v.variable_name as string)
            const dq = next.data_quality_rules ?? {}
            const table = dq.table ?? {}
            next.data_quality_rules = {
              ...dq,
              table: { ...table, primaryKeyFields: pkFromMetadata },
            }
          }
        }

        return next
      })
      setTerminologyValidationFailed(false)
      setSaveValidationError(null)
    }

    const navigateToEditorField = (tabId: string, field?: string) => {
      setActiveTab(tabId)
      onTabChange?.(tabId)
      requestAnimationFrame(() => {
        const target = field
          ? document.querySelector(`[data-field="${field}"]`)
          : document.querySelector(".editor-content")
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" })
          target.classList.add("ring-2", "ring-red-400", "rounded", "bg-red-50")
          setTimeout(
            () => target.classList.remove("ring-2", "ring-red-400", "rounded", "bg-red-50"),
            4000,
          )
        }
      })
    }

    const handleSave = async () => {
      console.log(
        "[DTAEditor] Save clicked - dataQualityValid:",
        dataQualityValid,
        "hasChanges:",
        hasChanges,
        "onSave:",
        !!onSave,
      )
      if (dataQualityValid === false) {
        console.log("[DTAEditor] Validation errors:", dataQualityErrors)
        setSaveValidationError(
          "Data Quality Rules tab has validation errors that must be fixed before saving.",
        )
        setActiveTab("data-quality-rules")
        onTabChange?.("data-quality-rules")
        requestAnimationFrame(() => {
          const firstError = document.querySelector(".editor-content .border-red-500")
          if (firstError) {
            firstError.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        })
        return
      }
      // Validate metadata schema variables
      const schema = editedContent.transfer_metadata_schema as
        | { variables?: { variable_name?: string }[] }
        | undefined
      const variables = schema?.variables || []
      if (variables.length > 0) {
        const hasEmptyName = variables.some((v) => !(v.variable_name || "").trim())
        const names = variables
          .map((v) => (v.variable_name || "").trim().toLowerCase())
          .filter(Boolean)
        const hasDuplicateName = names.length !== new Set(names).size
        if (hasEmptyName || hasDuplicateName) {
          const parts: string[] = []
          if (hasEmptyName) parts.push("variables with empty names")
          if (hasDuplicateName) parts.push("duplicate variable names")
          setSaveValidationError(`Cannot save: ${parts.join(", ")} on the Metadata tab.`)
          setActiveTab("transfer-metadata")
          onTabChange?.("transfer-metadata")
          requestAnimationFrame(() => {
            const firstError = document.querySelector("input[value='']")
            if (firstError) {
              firstError.scrollIntoView({ behavior: "smooth", block: "center" })
            }
          })
          return
        }
      }
      // Validate codelists and test concepts
      const codeLists = editedContent.code_lists || []
      const testConcepts = editedContent.test_concepts || []
      const hasValidValues = (values: { code?: string }[]) =>
        values.some((v) => (v.code || "").trim())
      const hasInvalidCodelist = codeLists.some(
        (cl) => !(cl.name || "").trim() || !Array.isArray(cl.values) || !hasValidValues(cl.values),
      )
      const hasDuplicateCodelistNames = (() => {
        const names = codeLists.map((cl) => (cl.name || "").trim().toLowerCase()).filter(Boolean)
        return names.length !== new Set(names).size
      })()
      const hasInvalidTestConcept = testConcepts.some(testConceptHasNoValueColumn)
      if (hasInvalidCodelist || hasDuplicateCodelistNames || hasInvalidTestConcept) {
        setTerminologyValidationFailed(true)
        const errorParts: string[] = []
        if (hasInvalidCodelist) errorParts.push("codelists missing a name or values")
        if (hasDuplicateCodelistNames) errorParts.push("duplicate codelist names")
        if (hasInvalidTestConcept) errorParts.push("test concepts with no value column selected")
        setSaveValidationError(`Cannot save: ${errorParts.join(", ")}. Fix the highlighted items.`)
        const tabsWithErrors: Record<string, boolean> = {
          "test-concepts": hasInvalidTestConcept,
          codelists: hasInvalidCodelist || hasDuplicateCodelistNames,
        }
        const firstErrorTab = TAB_DEFINITIONS.find((tab) => tabsWithErrors[tab.id])
        if (firstErrorTab) {
          setActiveTab(firstErrorTab.id)
          onTabChange?.(firstErrorTab.id)
        }
        setTimeout(() => {
          const firstInvalid = document.querySelector(
            "[data-codelist-item] .border-red-500, [data-terminology-item] .border-red-500, [data-terminology-item][data-invalid='true']",
          )
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }, 100)
        return
      }
      setTerminologyValidationFailed(false)
      // Warn-but-allow: a blank/unsupported date format surfaces in the banner but a DRAFT
      // still saves. Submit-for-review stays blocked (client + backend). Flipping this to a
      // hard block is a one-line change: `return` here when dateErrors is non-empty (#1252).
      const dateErrors = validateDateFormats(editedContent)
      setSaveValidationError(
        dateErrors.length > 0 ? dateErrors.map((e) => e.message).join(" ") : null,
      )
      if (onSave && hasChanges) {
        try {
          await onSave(editedContent)
          setHasChanges(false)
          setServerTableNameError(undefined)
          showToast({ subject: "Changes saved", variant: "success" })
        } catch (err) {
          const message = parseApiErrorMessage(err, "Could not save changes")
          const originalTableName = dta.document_content?.data_quality_rules?.table?.name
          const editedTableName = editedContent.data_quality_rules?.table?.name
          const changedTableName = originalTableName !== editedTableName
          const isDraftStageError =
            changedTableName && /only be edited in DRAFT stage/i.test(message)

          if (isTableNameSaveError(message) || isDraftStageError) {
            setServerTableNameError(message)
            setSaveValidationError(message)
            navigateToEditorField("data-quality-rules", "data_quality_rules.table.name")
          }
          showToast({
            subject: "Save failed",
            message,
            variant: "error",
          })
        }
      }
    }

    useImperativeHandle(ref, () => ({
      getContent: () => editedContent,
      triggerSave: handleSave,
      navigateToTab: navigateToEditorField,
      validateForPublish: () => {
        const errors: Array<{ field: string; tab: string; message: string }> = []

        // 1. Save-level validation: DQ form structural errors
        if (dataQualityValid === false) {
          errors.push({
            field: "data_quality_rules",
            tab: "data-quality-rules",
            message: "Data Quality Rules tab has validation errors that must be fixed",
          })
        }

        // 2. Save-level validation: metadata variable names
        const schema = editedContent.transfer_metadata_schema as
          | { variables?: { variable_name?: string; format?: string; data_format?: string }[] }
          | undefined
        const variables = schema?.variables || []
        if (variables.length > 0) {
          const hasEmptyName = variables.some((v) => !(v.variable_name || "").trim())
          const names = variables
            .map((v) => (v.variable_name || "").trim().toLowerCase())
            .filter(Boolean)
          const hasDuplicateName = names.length !== new Set(names).size
          if (hasEmptyName)
            errors.push({
              field: "transfer_metadata_schema.variables",
              tab: "transfer-metadata",
              message: "Some variables have empty names",
            })
          if (hasDuplicateName)
            errors.push({
              field: "transfer_metadata_schema.variables",
              tab: "transfer-metadata",
              message: "Duplicate variable names exist",
            })
          // Date-format readiness runs through the shared validator so the mounted
          // editor, the Overview tab, and the backend all apply the same rule (#1252).
          errors.push(...validateDateFormats(editedContent))
        }

        // 3. Publish readiness: required fields must be non-empty
        if (variables.length === 0) {
          errors.push({
            field: "transfer_metadata_schema.variables",
            tab: "transfer-metadata",
            message: "At least one metadata variable must be defined",
          })
        }

        const dqRules = (editedContent.data_quality_rules || {}) as Record<string, unknown>
        const table = (dqRules.table || {}) as Record<string, unknown>
        if (!((table.name as string) || "").trim()) {
          errors.push({
            field: "data_quality_rules.table.name",
            tab: "data-quality-rules",
            message: "Table name is required in Data Quality Rules",
          })
        }
        const pkFields = table.primaryKeyFields
        if (!(Array.isArray(pkFields) ? pkFields.length > 0 : !!pkFields)) {
          errors.push({
            field: "data_quality_rules.table.primaryKeyFields",
            tab: "data-quality-rules",
            message: "At least one primary key field is required",
          })
        }

        // Navigate to first error tab and highlight the field
        if (errors.length > 0) {
          setActiveTab(errors[0].tab)
          onTabChange?.(errors[0].tab)
          requestAnimationFrame(() => {
            // Highlight ALL fields with errors
            for (const err of errors) {
              const el = document.querySelector(`[data-field="${err.field}"]`)
              if (el) {
                el.classList.add("ring-2", "ring-red-400", "rounded", "bg-red-50")
                // Add error text below the field if not already present
                if (!el.querySelector(".publish-error-msg")) {
                  const msg = document.createElement("div")
                  msg.className = "publish-error-msg"
                  msg.style.cssText = "color: #d13212; font-size: 12px; margin-top: 4px;"
                  msg.textContent = err.message
                  el.appendChild(msg)
                }
              }
            }
            // Scroll to the first one
            const target = document.querySelector(`[data-field="${errors[0].field}"]`)
            target?.scrollIntoView({ behavior: "smooth", block: "center" })
          })
        }

        return errors
      },
    }))

    const handleEditorTabChange = (tabId: string) => {
      setActiveTab(tabId)
      onTabChange?.(tabId)
    }

    // Track which tabs have been modified
    const originalContent = dta.document_content || {}
    const dirtyTabs = new Set<string>([
      ...(JSON.stringify(editedContent.user_guide) !== JSON.stringify(originalContent.user_guide)
        ? ["user-guide"]
        : []),
      ...(JSON.stringify(editedContent.transfer_metadata) !==
      JSON.stringify(originalContent.transfer_metadata)
        ? ["data-ingestion"]
        : []),
      ...(JSON.stringify(editedContent.transfer_metadata_schema) !==
      JSON.stringify(originalContent.transfer_metadata_schema)
        ? ["transfer-metadata"]
        : []),
      ...(JSON.stringify(editedContent.code_lists) !== JSON.stringify(originalContent.code_lists)
        ? ["codelists"]
        : []),
      ...(JSON.stringify(editedContent.test_concepts) !==
      JSON.stringify(originalContent.test_concepts)
        ? ["test-concepts"]
        : []),
    ])

    const tabsWithValidationErrors = new Set<string>([
      ...(!dataQualityValid ? ["data-quality-rules"] : []),
      ...(terminologyValidationFailed ? ["codelists", "test-concepts"] : []),
      ...(saveValidationError?.includes("Metadata tab") ? ["transfer-metadata"] : []),
    ])

    const getTabLabel = (tab: TabDefinition) => {
      if (!inlineDiff) return tab.label
      const countMap: Record<string, number> = {
        "transfer-metadata": inlineDiff.schema.size,
        codelists: inlineDiff.codelists.size,
        "data-quality": inlineDiff.dqRules.size,
        "test-concepts": inlineDiff.testConcepts.size,
      }
      const count = countMap[tab.id] || 0
      return count > 0 ? `${tab.label} (${count})` : tab.label
    }

    const activeTabDef = TAB_DEFINITIONS.find((tab) => tab.id === activeTab)
    const ActiveTabComponent = activeTabDef?.component || UserGuideTab
    const activeTabLabel = activeTabDef?.label || "Editor"

    return (
      <>
        <EditorTabBridge onSwitchTab={handleEditorTabChange} />
        <div className={`dta-editor ${isSaving ? "cursor-wait" : ""}`}>
          <Container>
            <div className="editor-body flex">
              {/* Left Sidebar - Tab Navigation. `editor-sidebar` (DTAEditor.css, owned by #462)
                supplies the sticky positioning + bounded scroll; the border/background are set
                here via Cloudscape tokens now that the Tailwind border/bg utilities are gone. */}
              <nav
                aria-label="DTA editor sections"
                className="editor-sidebar"
                style={{
                  borderInlineEnd: `1px solid ${colorBorderDividerDefault}`,
                  background: colorBackgroundContainerContent,
                  paddingInlineEnd: spaceScaledXs,
                }}
              >
                <SpaceBetween size="l">
                  {(["instructional", "required"] as const).map((category) => (
                    <div key={category}>
                      <Box
                        fontSize="body-s"
                        fontWeight="bold"
                        color="text-body-secondary"
                        padding={{ bottom: "xxs" }}
                      >
                        {category === "instructional" ? "ADMINISTRATIVE TABS" : "REQUIRED TABS"}
                      </Box>
                      <SpaceBetween size="xxs">
                        {TAB_DEFINITIONS.filter((tab) => tab.category === category).map((tab) => (
                          <EditorNavItem
                            key={tab.id}
                            tab={tab}
                            label={getTabLabel(tab)}
                            active={activeTab === tab.id}
                            hasError={tabsWithValidationErrors.has(tab.id)}
                            isDirty={dirtyTabs.has(tab.id)}
                            onSelect={() => handleEditorTabChange(tab.id)}
                          />
                        ))}
                      </SpaceBetween>
                    </div>
                  ))}
                </SpaceBetween>
              </nav>

              {/* Main Content Area */}
              <div className="editor-content">
                <SpaceBetween size="l">
                  <Header
                    variant="h2"
                    description={activeTabDef?.description}
                    info={<SectionCommentBar />}
                  >
                    {activeTabLabel}
                  </Header>
                  {saveValidationError && (
                    <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                      <Icon name="status-warning" size="normal" variant="error" />
                      <button
                        className="flex-1 cursor-pointer text-left text-sm text-red-700 underline decoration-red-300 hover:decoration-red-500"
                        onClick={() => {
                          const el = document.querySelector(".editor-content .border-red-500")
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
                        }}
                      >
                        {saveValidationError}
                      </button>
                      <button
                        onClick={() => setSaveValidationError(null)}
                        className="text-red-400 hover:text-red-600"
                        aria-label="Dismiss"
                      >
                        <Icon name="close" size="normal" />
                      </button>
                    </div>
                  )}
                  {/* Boundary sits around the tab body only, not the whole editor, so a tab that
                    fails to render still leaves the sidebar navigation and header usable and the
                    user can move to another tab. Keyed on the active tab so switching tabs clears
                    a previous tab's error. */}
                  <ErrorBoundary section={activeTabLabel} resetKey={activeTab}>
                    {activeTab === "data-quality-rules" ? (
                      <DataQualityTab
                        dta={dta}
                        content={editedContent}
                        onUpdate={handleUpdate}
                        canEdit={canEdit}
                        tableNameLocked={tableNameLocked}
                        serverTableNameError={serverTableNameError}
                        onTableNameChange={() => {
                          setServerTableNameError(undefined)
                          setSaveValidationError(null)
                        }}
                        inlineDiff={inlineDiff}
                        onValidationChange={(isValid, errors) => {
                          setDataQualityValid(isValid)
                          setDataQualityErrors(errors)
                        }}
                        onNavigateToMetadata={
                          canEdit ? () => handleEditorTabChange("transfer-metadata") : undefined
                        }
                      />
                    ) : (
                      <ActiveTabComponent
                        dta={dta}
                        content={editedContent}
                        onUpdate={handleUpdate}
                        canEdit={canEdit}
                        showErrors={terminologyValidationFailed}
                        inlineDiff={inlineDiff}
                      />
                    )}
                  </ErrorBoundary>
                </SpaceBetween>
              </div>
            </div>
          </Container>
        </div>
      </>
    )
  },
)

export default DTAEditor
